<script setup lang="ts">
/**
 * UiFilterChip - Design System Filter Chip Component
 *
 * A removable chip/tag used to display active filters.
 * Matches the styling of the live platform filter-chip.
 *
 * @example
 * <UiFilterChip
 *   label="Category"
 *   value="Electronics"
 *   @remove="handleRemove"
 * />
 */
import { Icon } from '@iconify/vue';

export interface UiFilterChipProps {
   /** The field/category label */
   label: string;
   /** The filter value */
   value: string;
   /** Secondary value for range filters (e.g., date ranges) */
   secondaryValue?: string;
   /** Whether the chip is editable on click */
   editable?: boolean;
   /** Whether the chip appears in a "stale" warning state */
   stale?: boolean;
   /** Whether the chip is disabled */
   disabled?: boolean;
}

const props = withDefaults(defineProps<UiFilterChipProps>(), {
   editable: true,
   stale: false,
   disabled: false,
});

const emit = defineEmits<{
   edit: [];
   remove: [];
}>();

function handleClick() {
   if (props.editable && !props.disabled) {
      emit('edit');
   }
}

function handleRemove(event: Event) {
   event.stopPropagation();
   if (!props.disabled) {
      emit('remove');
   }
}

function handleKeydown(event: KeyboardEvent) {
   if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
   }
}
</script>

<template>
   <div
      role="button"
      :tabindex="editable && !disabled ? 0 : -1"
      :aria-disabled="disabled ? 'true' : undefined"
      class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
      transition-colors text-sm group focus:outline-none focus:ring-2 focus:ring-primary/50"
      :class="[
         stale
            ? 'bg-amber-50 border border-amber-400'
            : 'bg-neutral border border-black/10',
         disabled
            ? 'opacity-50 cursor-not-allowed'
            : editable
               ? 'hover:bg-neutral-dark/30 cursor-pointer'
               : 'cursor-default',
      ]"
      :aria-label="`${editable ? 'Edit filter' : 'Filter'}: ${label} ${value}${secondaryValue ? ` - ${secondaryValue}` : ''}${stale ? ' (stale)' : ''}`"
      :title="stale ? 'This filter may not apply to the current selection' : undefined"
      @click="handleClick"
      @keydown="handleKeydown"
   >
      <!-- Stale warning icon -->
      <Icon
         v-if="stale"
         icon="mdi:alert-circle-outline"
         class="size-4 text-amber-600 shrink-0"
      />

      <!-- Label -->
      <span :class="stale ? 'text-amber-800/70' : 'text-black/70'">
         {{ label }}:
      </span>

      <!-- Value -->
      <span class="font-medium text-black">
         {{ secondaryValue ? `${value} - ${secondaryValue}` : value }}
      </span>

      <!-- Remove button -->
      <button
         type="button"
         class="ml-1 p-1 rounded-full hover:bg-black/10 text-black/40 hover:text-black/70
        transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
         :class="disabled ? 'cursor-not-allowed opacity-50' : ''"
         :disabled="disabled"
         title="Remove filter"
         :aria-label="`Remove filter: ${label}`"
         @click="handleRemove"
      >
         <Icon icon="mdi:filter-remove" class="size-4 pointer-events-none" />
      </button>
   </div>
</template>
